import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../context/AuthContext';
import * as paymentService from '../../services/paymentService';
import { convertRandsToUnits, formatUnits, getRateForDate, hasValidRates } from '../../config/siteConfig';
import { useSiteRates } from '../../context/SiteRatesContext';

/**
 * A card component displaying recent prepaid transactions with caching.
 * For sites with a runtime kWh rate config, displays a Units column showing
 * kWh based on the historical rate at each transaction's date.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.transactions - Array of transaction objects (optional, will fetch if not provided)
 * @param {string} props.accountUuid - Account UUID for fetching transactions (optional)
 * @param {boolean} props.useCache - Whether to use cache (default: true)
 * @param {string} props.siteName - The site name (used for site-specific display)
 */
const RecentPrepaidTransactionsCard = ({ 
  transactions: initialTransactions = [], 
  accountUuid = null,
  useCache = true,
  siteName = null
}) => {
  const [transactions, setTransactions] = useState(initialTransactions);
  const [loading, setLoading] = useState(initialTransactions.length === 0);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  // Get the account UUID to use
  const effectiveAccountUuid = accountUuid || user?.accountUuid;
  
  // Sites with a runtime rate config show a Units (kWh) column.
  const { getSiteConfig } = useSiteRates();
  const siteConfig = getSiteConfig(siteName);
  const isKwh = hasValidRates(siteConfig);

  // Cache key helper function
  const getCacheKey = (uuid) => `prepaid_transactions_${uuid}`;

  // Check if cache is stale
  const isCacheStale = () => sessionStorage.getItem('transaction_cache_stale') === 'true';

  // Helper to check if cached data is still valid (10 minutes TTL)
  const isCacheValid = (timestamp) => {
    const tenMinutesInMs = 10 * 60 * 1000;
    return Date.now() - timestamp < tenMinutesInMs;
  };

  // Fetch transactions with caching
  useEffect(() => {
    const fetchTransactions = async () => {
      // Skip if we already have transactions or no account UUID
      if (initialTransactions.length > 0 || !effectiveAccountUuid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check for stale cache flag first
        if (isCacheStale()) {
          // console.log('Transaction cache marked as stale, fetching fresh data');
          sessionStorage.removeItem('transaction_cache_stale');
          // Continue with API request below
        } else if (useCache) {
          // Check cache if not stale
          const cacheKey = getCacheKey(effectiveAccountUuid);
          const cachedData = sessionStorage.getItem(cacheKey);
          
          if (cachedData) {
            try {
              const parsedCache = JSON.parse(cachedData);
              if (isCacheValid(parsedCache.timestamp)
                  && Array.isArray(parsedCache.data) && parsedCache.data.length > 0) {
                // console.log('Using cached transaction data');
                setTransactions(parsedCache.data);
                setLoading(false);
                return;
              } else {
                // console.log('Cached transaction data expired or empty, fetching fresh');
              }
            } catch (e) {
              // console.error('Error parsing cached transaction data:', e);
            }
          }
        }

        // Fetch from API
        // console.log('Fetching fresh transaction data for account:', effectiveAccountUuid);
        const data = await paymentService.getTransactionsByAccount(effectiveAccountUuid);
        
        if (data && Array.isArray(data)) {
          setTransactions(data);
          
          // Only cache non-empty results to avoid locking out future fetches
          if (useCache && data.length > 0) {
            try {
              const cacheData = {
                data: data,
                timestamp: Date.now()
              };
              sessionStorage.setItem(getCacheKey(effectiveAccountUuid), JSON.stringify(cacheData));
            } catch (e) {
              // console.error('Error saving transaction data to cache:', e);
            }
          }
        } else {
          // console.warn('Invalid transaction data format:', data);
          setTransactions([]);
        }
      } catch (err) {
        // console.error('Error fetching transactions:', err);
        setError('Failed to load transaction history');
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [effectiveAccountUuid, initialTransactions, useCache]);

  // Format date string if needed
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-ZA', {
        year: 'numeric', month: 'short', day: 'numeric' 
      });
    } catch (e) {
      return dateString; // Return original if formatting fails
    }
  };

  // Format time string
  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleTimeString('en-ZA', {
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (e) {
      return '';
    }
  };

  // Format monetary amount properly
  const formatAmount = (amount) => {
    if (typeof amount !== 'number') return 'R 0.00';
 
    const formattedAmount = (amount).toFixed(2);
    return `R ${formattedAmount}`;
  };

  // Calculate units for a transaction using historical rate at time of purchase
  const calculateUnitsForTransaction = (transaction) => {
    if (!hasValidRates(siteConfig) || typeof transaction.amount !== 'number') return null;

    const transactionDate = transaction.createdAt || transaction.date || transaction.transactionDate;
    const rate = getRateForDate(transactionDate, siteConfig);
    const units = convertRandsToUnits(transaction.amount, rate.peak);
    return formatUnits(units);
  };

  // Get status color
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'succeeded':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'created':
        return 'bg-gray-100 text-gray-600';
      case 'refunded':
        return 'bg-gray-200 text-gray-800';
      case 'failed':
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Sort transactions by date (newest first)
  const sortedTransactions = [...transactions].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.date || 0);
    const dateB = new Date(b.createdAt || b.date || 0);
    return dateB - dateA;
  });

  // Handle manual refresh of transaction data
  const refreshTransactions = async () => {
    if (!effectiveAccountUuid) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Clear the cache for this account
      sessionStorage.removeItem(getCacheKey(effectiveAccountUuid));
      
      // Fetch fresh data
      const data = await paymentService.getTransactionsByAccount(effectiveAccountUuid);
      
      if (data && Array.isArray(data)) {
        setTransactions(data);
        
        if (useCache && data.length > 0) {
          try {
            const cacheData = {
              data: data,
              timestamp: Date.now()
            };
            sessionStorage.setItem(getCacheKey(effectiveAccountUuid), JSON.stringify(cacheData));
          } catch (e) {
            // console.error('Error saving refreshed transaction data to cache:', e);
          }
        }
      } else {
        setTransactions([]);
      }
    } catch (err) {
      // console.error('Error refreshing transactions:', err);
      setError('Failed to refresh transaction history');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card bg-beige-40 shadow-md rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Recent Transactions</h2>
        <button 
          onClick={refreshTransactions}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
          disabled={loading}
        >
          {loading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Refreshing...
            </span>
          ) : (
            <span className="flex items-center">
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </span>
          )}
        </button>
      </div>
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error}</p>
        </div>
      )}
      
      {loading && !transactions.length ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          <span className="ml-2">Loading transactions...</span>
        </div>
      ) : sortedTransactions.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                {isKwh && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Units (kWh)
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedTransactions.slice(0, 5).map((transaction, index) => (
                <tr key={transaction.id || transaction.uuid || index}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {formatDate(transaction.createdAt || transaction.date || transaction.transactionDate)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {formatTime(transaction.createdAt || transaction.date || transaction.transactionDate)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {transaction.paymentMethodType || transaction.type || transaction.transactionType || 'N/A'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatAmount(transaction.amount)}
                  </td>
                  {isKwh && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {calculateUnitsForTransaction(transaction)}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(transaction.status)}`}>
                      {transaction.status || 'Unknown'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500">No recent transactions available.</p>
      )}
    </div>
  );
};

RecentPrepaidTransactionsCard.propTypes = {
  transactions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    uuid: PropTypes.string,
    date: PropTypes.string,
    createdAt: PropTypes.string,
    transactionDate: PropTypes.string,
    type: PropTypes.string,
    paymentMethodType: PropTypes.string,
    transactionType: PropTypes.string,
    amount: PropTypes.number,
    status: PropTypes.string
  })),
  accountUuid: PropTypes.string,
  useCache: PropTypes.bool,
  siteName: PropTypes.string
};

export default RecentPrepaidTransactionsCard; 